import React from "react";
import { Link } from "react-router-dom";
import { Waves, Play, ArrowRight, GraduationCap, Sparkles, Dumbbell } from "lucide-react";
import { LESSONS, playNote, audioSupported } from "../lib/synthLab";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import EnvelopeGraph from "../components/EnvelopeGraph";
import WaveShape from "../components/synthlab/WaveShape";

const CAN_PLAY = audioSupported();

// One playable example: plays a held note so attack/release are audible, with
// an optional diagram (waveform shape for the oscillator, ADSR curve for the
// envelope lessons) so the change is visible as well as audible.
const ExampleButton = ({ label, patch, viz }) => (
  <button
    type="button"
    onClick={() => playNote(patch, 60, 1100)}
    disabled={!CAN_PLAY}
    className="group flex flex-col gap-1 rounded-lg border border-border bg-background px-3 py-2 text-left transition-colors hover:border-primary/60 hover:bg-accent disabled:opacity-50"
  >
    {viz === "wave" && <WaveShape waveform={patch.waveform} />}
    {viz === "envelope" && <EnvelopeGraph patch={patch} height={40} />}
    <span className="flex items-center gap-1.5 text-sm font-medium">
      <Play className="h-3.5 w-3.5 text-primary" />
      {label}
    </span>
  </button>
);

const SynthBasics = () => {
  return (
    <div className="relative min-h-[calc(100vh-4rem)] overflow-hidden bg-dreamy">
      <div
        aria-hidden
        className="pointer-events-none absolute -left-24 -top-24 h-96 w-96 rounded-full bg-primary/15 blur-3xl animate-aurora"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-40 -right-24 h-[28rem] w-[28rem] rounded-full bg-[hsl(var(--blue)/0.22)] blur-3xl animate-aurora"
        style={{ animationDelay: "-7s" }}
      />

      <div className="container relative mx-auto max-w-3xl px-4 py-12">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="inline-flex items-center gap-1.5 text-sm font-medium text-primary">
              <GraduationCap className="h-4 w-4" />
              synth basics
            </p>
            <h1 className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">
              <span className="text-gold-shimmer">How a synth works</span>
            </h1>
            <p className="mt-2 max-w-xl text-muted-foreground">
              A quick tour of every control — what it does, the word for it, and a
              few examples to play. Then go put it to use in the Lab.
            </p>
          </div>
          <Button asChild size="sm">
            <Link to="/synth-lab">
              Open the Lab
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>

        {!CAN_PLAY && (
          <p className="mt-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Your browser doesn't support Web Audio, so the examples won't play —
            the explanations still hold.
          </p>
        )}

        <div className="mt-6 rounded-xl border border-border bg-card/70 p-4 text-sm text-muted-foreground backdrop-blur">
          <span className="font-medium text-card-foreground">The big idea:</span>{" "}
          start with a raw <em>waveform</em>, carve its brightness with a{" "}
          <em>filter</em>, then shape its volume over time with an{" "}
          <em>envelope</em> (ADSR). That's subtractive synthesis — and it's most
          of what these knobs do.
        </div>

        <div className="mt-6 space-y-5">
          {LESSONS.map((lesson) => (
            <Card key={lesson.id} className="bg-card/80 backdrop-blur">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Sparkles className="h-4 w-4 text-primary" />
                  {lesson.title}
                </CardTitle>
                <CardDescription className="text-primary/80">{lesson.tagline}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm leading-relaxed text-muted-foreground">{lesson.body}</p>
                {CAN_PLAY && (
                  <>
                    <p className="mt-4 text-xs font-medium uppercase tracking-wide text-primary/70">
                      {lesson.listen}
                    </p>
                    <div
                      className={
                        lesson.viz === "wave"
                          ? "mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4"
                          : "mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3"
                      }
                    >
                      {lesson.examples.map((ex) => (
                        <ExampleButton key={ex.label} label={ex.label} patch={ex.patch} viz={lesson.viz} />
                      ))}
                    </div>
                  </>
                )}
                <div className="mt-4">
                  <Button asChild size="sm" variant="outline">
                    <Link to={`/synth-lab/practice?focus=${lesson.id}`}>
                      <Dumbbell className="h-4 w-4" />
                      Practice this
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-8 flex flex-col items-center gap-3 rounded-xl border border-border bg-card/70 p-6 text-center backdrop-blur">
          <Waves className="h-6 w-6 text-primary" />
          <p className="text-sm text-muted-foreground">
            That's the whole toolkit. Now train your ears with it.
          </p>
          <Button asChild>
            <Link to="/synth-lab">
              Open the Synth Lab
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SynthBasics;
